import React, { useState } from 'react';

const WorkCaseDetail = ({
  workCase,
  onClose,
  onUpdateStatus,
  onApproveReject,
  onAddNote,
  currentUser = null
}) => {
  const [newNote, setNewNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('vi-VN');
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: 'Chờ xử lý', color: 'bg-yellow-100 text-yellow-800' },
      accepted: { label: 'Đã nhận', color: 'bg-blue-100 text-blue-800' },
      in_progress: { label: 'Đang thực hiện', color: 'bg-orange-100 text-orange-800' },
      completed: { label: 'Chờ duyệt', color: 'bg-purple-100 text-purple-800' },
      approved: { label: 'Hoàn thành', color: 'bg-green-100 text-green-800' },
      rejected: { label: 'Từ chối', color: 'bg-red-100 text-red-800' }
    };

    const config = statusConfig[status] || { label: status, color: 'bg-gray-100 text-gray-800' };
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  // Get priority badge
  const getPriorityBadge = (priority) => {
    const priorityConfig = {
      low: { label: 'Thấp', color: 'text-green-600' },
      medium: { label: 'Trung bình', color: 'text-yellow-600' },
      high: { label: 'Cao', color: 'text-orange-600' },
      urgent: { label: 'Khẩn cấp', color: 'text-red-600' }
    };

    const config = priorityConfig[priority] || { label: priority, color: 'text-gray-600' };
    
    return (
      <span className={`text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  // Check if deadline is overdue
  const isOverdue = (deadline) => {
    return new Date(deadline) < new Date();
  };

  // Handle status update
  const handleStatusUpdate = async (assigneeId, status, notes = '') => {
    try {
      setSubmitting(true);
      const workCaseId = (workCase.workCase?.id || workCase.id);
      await onUpdateStatus(workCaseId, assigneeId, status, notes);
      // Optimistic: cập nhật ngay UI chi tiết
      if (status === 'completed') {
        // cập nhật nhãn "Chờ duyệt"
        // phần thực tế ở parent đã setSelectedCase phù hợp
      }
    } catch (error) {
      console.error('Error updating status:', error);
    } finally {
      setSubmitting(false);
    }
  };

  // Handle approve/reject
  const handleApproveReject = async (action, notes = '') => {
    try {
      setSubmitting(true);
      const workCaseId = workCase.workCase?.id || workCase.id;
      await onApproveReject(workCaseId, action, notes);
    } catch (error) {
      console.error('Error approving/rejecting:', error);
    } finally {
      setSubmitting(false);
    }
  };

  // Handle add note
  const handleAddNote = async (e) => {
    e.preventDefault();
    if (!newNote.trim()) return;

    const workCaseId = workCase.workCase?.id || workCase.id;
    if (!workCaseId) {
      console.error('Work case ID is undefined');
      return;
    }

    console.log('WorkCaseDetail handleAddNote called with workCaseId:', workCaseId, 'note:', newNote);

    try {
      setSubmitting(true);
      await onAddNote(workCaseId, newNote);
      setNewNote('');
    } catch (error) {
      console.error('Error adding note:', error);
    } finally {
      setSubmitting(false);
    }
  };

  // Derive unified assignments list that also includes main assignee from work_cases
  const normalizeId = (v) => (v == null ? null : Number(v));
  const wc = workCase.workCase || workCase; // normalize data shape
  const hasAssignmentsArray = Array.isArray(workCase.assignments) && workCase.assignments.length > 0;
  const mainAssignee = wc?.assignee_id ? {
    id: 'main',
    assignee_id: normalizeId(wc.assignee_id),
    assignee_name: wc.assignee_name,
    assignee_email: wc.assignee_email,
    status: wc.status,
    assigned_at: wc.created_at,
    completed_at: wc.status === 'completed' ? (wc.updated_at || wc.created_at) : null
  } : null;

  const assignmentsIncludeMain = hasAssignmentsArray && workCase.assignments.some(a => normalizeId(a.assignee_id) === normalizeId(wc?.assignee_id));
  const allAssignments = hasAssignmentsArray
    ? (assignmentsIncludeMain || !mainAssignee ? workCase.assignments.map(a => ({...a, assignee_id: normalizeId(a.assignee_id)})) : [mainAssignee, ...workCase.assignments.map(a => ({...a, assignee_id: normalizeId(a.assignee_id)}))])
    : (mainAssignee ? [mainAssignee] : []);

  // Check if current user is an assignee (main or any assignment)
  const currentUserId = normalizeId(currentUser?.id);
  const isAssignee = allAssignments.some(a => normalizeId(a.assignee_id) === currentUserId);
  const userAssignment = allAssignments.find(a => normalizeId(a.assignee_id) === currentUserId);

  // For backward-compat text helpers
  const virtualAssignment = !hasAssignmentsArray && mainAssignee && normalizeId(mainAssignee.assignee_id) === currentUserId ? mainAssignee : null;

  // Check if user is creator
  const isCreator = normalizeId(wc?.creator_id) === currentUserId;

  // Debug log
  console.log('🔍 [WorkCaseDetail] ===== DEBUG START =====');
  console.log('WorkCaseDetail render - workCase:', workCase);
  console.log('WorkCaseDetail render - wc.id (normalized):', wc?.id);
  console.log('WorkCaseDetail render - currentUser:', currentUser);
  console.log('WorkCaseDetail render - isAssignee:', isAssignee);
  console.log('WorkCaseDetail render - isCreator:', isCreator);
  console.log('WorkCaseDetail render - userAssignment:', userAssignment);
  console.log('WorkCaseDetail render - workCase.assignments:', workCase.assignments);
  console.log('WorkCaseDetail render - wc.assignee_id:', wc?.assignee_id);
  console.log('WorkCaseDetail render - wc.creator_id:', wc?.creator_id);
  
  // Debug button visibility logic
  console.log('🔍 [WorkCaseDetail] Button visibility analysis:');
  if (isAssignee && userAssignment) {
    console.log('  ✅ User is assignee, assignment status:', userAssignment.status);
    console.log('  📋 Should show buttons for status:', userAssignment.status);
  } else if (isAssignee && workCase.assignee_id === currentUser?.id) {
    console.log('  ✅ User is main assignee, work case status:', workCase.status);
    console.log('  📋 Should show buttons for status:', workCase.status);
  } else if (isAssignee && virtualAssignment) {
    console.log('  ✅ User is virtual assignee, status:', virtualAssignment.status);
    console.log('  📋 Should show buttons for status:', virtualAssignment.status);
  } else {
    console.log('  ❌ User is not assignee');
  }
  
  if (isCreator) {
    console.log('  ✅ User is creator');
    const hasCompletedAssignment = workCase.assignments?.some(a => a.status === 'completed') || 
                                  workCase.workCase?.status === 'completed' || 
                                  workCase.status === 'completed';
    console.log('  📋 Has completed assignment:', hasCompletedAssignment);
    if (hasCompletedAssignment) {
      console.log('  ✅ Should show approve/reject buttons');
    }
  } else {
    console.log('  ❌ User is not creator');
  }
  console.log('🔍 [WorkCaseDetail] ===== DEBUG END =====');

  // Timeline helpers
  const getEventColor = (event) => {
    switch (event) {
      case 'case_created':
        return 'bg-blue-500';
      case 'assignment_created':
        return 'bg-green-500';
      case 'assignment_status_changed':
        return 'bg-purple-500';
      case 'case_status_changed':
        return 'bg-orange-500';
      case 'note_added':
        return 'bg-gray-400';
      default:
        return 'bg-gray-300';
    }
  };

  const renderEventText = (n) => {
    const actor = n.user_name || 'Hệ thống';
    const assigneeName = n.target_assignee_name;
    switch (n.event) {
      case 'case_created':
        return `Case được tạo bởi ${actor}`;
      case 'assignment_created':
        return `Giao cho ${assigneeName || 'người nhận'}`;
      case 'assignment_status_changed':
        return `${assigneeName || 'Người nhận'} chuyển ${n.status_from || ''} → ${n.status_to || ''}`;
      case 'case_status_changed':
        return `Case chuyển ${n.status_from || ''} → ${n.status_to || ''}`;
      case 'note_added':
        return `${actor} thêm ghi chú`;
      default:
        // fallback cho bản ghi cũ không có event/type
        return n.note || 'Cập nhật';
    }
  };

  const notesArray = Array.isArray(workCase.notes) ? workCase.notes : [];
  const timelineNotes = notesArray
    .filter(n => (n.type || 'user') === 'system' || n.event)
    .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  const userNotes = notesArray.filter(n => (n.type || 'user') === 'user');

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex justify-between items-start">
            <div className="flex-1">
                             <h2 className="text-xl font-semibold text-gray-900 mb-2">
                 {workCase.workCase?.title || workCase.title}
               </h2>
               <div className="flex items-center space-x-3">
                 {getStatusBadge(workCase.workCase?.status || workCase.status)}
                 {getPriorityBadge(workCase.workCase?.priority || workCase.priority)}
                 <span className="text-sm text-gray-500">
                   Tạo bởi {workCase.workCase?.creator_name || workCase.creator_name}
                 </span>
               </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 ml-4"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Info */}
            <div className="lg:col-span-2">
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <h3 className="text-lg font-medium text-gray-900 mb-3">Thông tin chi tiết</h3>
                <div className="space-y-3 text-sm">
                                     <div>
                     <span className="font-medium text-gray-700">Mô tả:</span>
                     <p className="text-gray-600 mt-1">{workCase.workCase?.description || workCase.description}</p>
                   </div>
                   <div className="grid grid-cols-2 gap-4">
                     <div>
                       <span className="font-medium text-gray-700">Danh mục:</span>
                       <p className="text-gray-600">{workCase.workCase?.category || workCase.category}</p>
                     </div>
                     <div>
                       <span className="font-medium text-gray-700">Deadline:</span>
                       <p className={`${isOverdue(workCase.workCase?.deadline || workCase.deadline) ? 'text-red-600 font-medium' : 'text-gray-600'}`}>
                         {formatDate(workCase.workCase?.deadline || workCase.deadline)}
                       </p>
                     </div>
                     <div>
                       <span className="font-medium text-gray-700">Tạo lúc:</span>
                       <p className="text-gray-600">{formatDate(workCase.workCase?.created_at || workCase.created_at)}</p>
                     </div>
                     <div>
                       <span className="font-medium text-gray-700">Cập nhật lúc:</span>
                       <p className="text-gray-600">{formatDate(workCase.workCase?.updated_at || workCase.updated_at)}</p>
                     </div>
                   </div>
                </div>
              </div>

                             {/* Assignments */}
               <div className="bg-gray-50 rounded-lg p-4 mb-6">
                 <h3 className="text-lg font-medium text-gray-900 mb-3">Người được giao</h3>
                  <div className="space-y-3">
                    {allAssignments.map((assignment) => (
                     <div key={assignment.id} className="border border-gray-200 rounded-lg p-3">
                       <div className="flex items-center justify-between mb-2">
                         <div>
                           <p className="font-medium text-gray-900">{assignment.assignee_name}</p>
                           <p className="text-sm text-gray-500">{assignment.assignee_email}</p>
                         </div>
                         {getStatusBadge(assignment.status)}
                       </div>
                       <div className="text-sm text-gray-600">
                         <p>Giao lúc: {formatDate(assignment.assigned_at)}</p>
                         {assignment.completed_at && (
                           <p>Hoàn thành lúc: {formatDate(assignment.completed_at)}</p>
                         )}
                       </div>
                       
                       {/* Status update buttons for assignee */}
                        {normalizeId(assignment.assignee_id) === currentUserId && (
                         <div className="mt-3 pt-3 border-t border-gray-200">
                           <p className="text-xs text-gray-600 mb-2">Hành động của bạn:</p>
                           <div className="flex space-x-2">
                             {assignment.status === 'pending' && (
                               <>
                                 <button
                                   onClick={() => handleStatusUpdate(assignment.assignee_id, 'accepted')}
                                   disabled={submitting}
                                   className="px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 transition-colors disabled:opacity-50"
                                 >
                                   Nhận case
                                 </button>
                                 <button
                                   onClick={() => handleStatusUpdate(assignment.assignee_id, 'rejected')}
                                   disabled={submitting}
                                   className="px-3 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700 transition-colors disabled:opacity-50"
                                 >
                                   Từ chối
                                 </button>
                               </>
                             )}
                             {assignment.status === 'accepted' && (
                               <button
                                 onClick={() => handleStatusUpdate(assignment.assignee_id, 'in_progress')}
                                 disabled={submitting}
                                 className="px-3 py-1 bg-orange-600 text-white text-xs rounded hover:bg-orange-700 transition-colors disabled:opacity-50"
                               >
                                 Bắt đầu thực hiện
                               </button>
                             )}
                             {assignment.status === 'in_progress' && (
                               <button
                                 onClick={() => handleStatusUpdate(assignment.assignee_id, 'completed')}
                                 disabled={submitting}
                                 className="px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 transition-colors disabled:opacity-50"
                               >
                                 Hoàn thành
                               </button>
                             )}
                             {assignment.status === 'completed' && (
                               <p className="text-xs text-green-600 font-medium">
                                 ✅ Đã hoàn thành - Chờ người tạo case review
                               </p>
                             )}
                           </div>
                         </div>
                       )}
                     </div>
                   ))}
                    
                 </div>
               </div>

              {/* Notes */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-lg font-medium text-gray-900 mb-3">Ghi chú</h3>
                
                {/* Add new note */}
                <form onSubmit={handleAddNote} className="mb-4">
                  <div className="flex space-x-2">
                    <input
                      type="text"
                      value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                      placeholder="Thêm ghi chú..."
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      disabled={submitting}
                    />
                    <button
                      type="submit"
                      disabled={!newNote.trim() || submitting}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {submitting ? 'Đang gửi...' : 'Gửi'}
                    </button>
                  </div>
                </form>

                 {/* Notes list (only user notes) */}
                 <div className="space-y-3 max-h-64 overflow-y-auto">
                   {userNotes.length === 0 ? (
                     <p className="text-gray-500 text-sm">Chưa có ghi chú nào</p>
                   ) : (
                     userNotes.map((note) => (
                       <div key={note.id} className="bg-white rounded-lg p-3 border border-gray-200">
                         <div className="flex items-start justify-between mb-2">
                           <p className="font-medium text-gray-900">{note.user_name}</p>
                           <span className="text-xs text-gray-500">{formatDate(note.created_at)}</span>
                         </div>
                         <p className="text-gray-600 text-sm">{note.note}</p>
                       </div>
                     ))
                   )}
                 </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              {/* Actions */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <h3 className="text-lg font-medium text-gray-900 mb-3">Hành động</h3>
                
                                 {/* Approve/Reject buttons for creator */}
                 {isCreator && (
                   <div className="space-y-2">
                     <p className="text-sm text-gray-600 mb-2">Hành động cho người tạo case:</p>
                     
                     {/* Show approve/reject buttons when any assignment is completed */}
                      {(workCase.assignments?.some(a => a.status === 'completed') || 
                        workCase.workCase?.status === 'completed' || 
                        workCase.status === 'completed') && (
                       <>
                         <button
                           onClick={() => handleApproveReject('approve')}
                           disabled={submitting}
                           className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                         >
                            Chấp thuận (đánh dấu Hoàn thành)
                         </button>
                         <button
                           onClick={() => handleApproveReject('reject')}
                           disabled={submitting}
                           className="w-full px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                         >
                           Từ chối
                         </button>
                       </>
                     )}
                     
                     {/* Show status info */}
                     <div className="text-sm text-gray-500 mt-2">
                       <p>Trạng thái hiện tại: {getStatusBadge(workCase.workCase?.status || workCase.status)}</p>
                       {workCase.assignments && workCase.assignments.length > 0 && (
                         <div className="mt-1">
                           <p className="font-medium">Trạng thái assignments:</p>
                           {workCase.assignments.map((assignment, index) => (
                             <p key={index} className="text-xs">
                               {assignment.assignee_name}: {getStatusBadge(assignment.status)}
                             </p>
                           ))}
                         </div>
                       )}
                     </div>
                   </div>
                 )}

                                                   {/* Status info for assignee */}
                  {isAssignee && (userAssignment || workCase.assignee_id === currentUser?.id || virtualAssignment) && (
                    <div className="space-y-3">
                      <div className="text-sm">
                        <p className="font-medium text-gray-700">Trạng thái của bạn:</p>
                        <div className="mt-1">
                          {getStatusBadge(userAssignment?.status || virtualAssignment?.status || workCase.assignment_status || workCase.status)}
                        </div>
                      </div>
                      
                      {(userAssignment?.status === 'pending' || virtualAssignment?.status === 'pending' || workCase.assignment_status === 'pending' || workCase.status === 'pending') && (
                        <p className="text-xs text-gray-500">
                          Bạn cần nhận hoặc từ chối case này
                        </p>
                      )}
                      
                      {(userAssignment?.status === 'accepted' || virtualAssignment?.status === 'accepted' || workCase.assignment_status === 'accepted' || workCase.status === 'accepted') && (
                        <p className="text-xs text-gray-500">
                          Bạn đã nhận case, hãy bắt đầu thực hiện
                        </p>
                      )}
                      
                      {(userAssignment?.status === 'in_progress' || virtualAssignment?.status === 'in_progress' || workCase.assignment_status === 'in_progress' || workCase.status === 'in_progress') && (
                        <p className="text-xs text-gray-500">
                          Bạn đang thực hiện case này
                        </p>
                      )}
                      
                      {(userAssignment?.status === 'completed' || virtualAssignment?.status === 'completed' || workCase.assignment_status === 'completed' || workCase.status === 'completed') && (
                        <p className="text-xs text-gray-500">Bạn đã hoàn thành, chờ người tạo duyệt</p>
                      )}

                      {/* Cho phép nộp lại (làm lại) khi case bị người tạo từ chối, bất kể trạng thái hiện tại của assignment */}
                      {(workCase.workCase?.status === 'rejected' || workCase.status === 'rejected') && (
                        <button
                          onClick={() => handleStatusUpdate((userAssignment?.assignee_id || virtualAssignment?.assignee_id || workCase.assignee_id), 'in_progress')}
                          disabled={submitting}
                          className="w-full px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50"
                        >
                          Làm lại (chuyển về Đang thực hiện)
                        </button>
                      )}

                      {/* Quick actions for assignee */}
                      <div className="pt-2">
                        {(() => {
                          const target = userAssignment || virtualAssignment || { assignee_id: workCase.assignee_id, status: workCase.assignment_status || workCase.status };
                          const st = target?.status;
                          if (st === 'pending') {
                            return (
                              <div className="flex space-x-2">
                                <button
                                  onClick={() => handleStatusUpdate(target.assignee_id, 'accepted')}
                                  disabled={submitting}
                                  className="px-4 py-2 w-full bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                                >
                                  Nhận case
                                </button>
                                <button
                                  onClick={() => handleStatusUpdate(target.assignee_id, 'rejected')}
                                  disabled={submitting}
                                  className="px-4 py-2 w-full bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                                >
                                  Từ chối
                                </button>
                              </div>
                            );
                          }
                          if (st === 'accepted') {
                            return (
                              <button
                                onClick={() => handleStatusUpdate(target.assignee_id, 'in_progress')}
                                disabled={submitting}
                                className="w-full px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50"
                              >
                                Bắt đầu thực hiện
                              </button>
                            );
                          }
                          if (st === 'in_progress') {
                            return (
                              <button
                                onClick={() => handleStatusUpdate(target.assignee_id, 'completed')}
                                disabled={submitting}
                                className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                              >
                                Hoàn thành
                              </button>
                            );
                          }
                          return null;
                        })()}
                      </div>
                    </div>
                  )}

                                 {/* Info for creator */}
                 {isCreator && (
                   <div className="text-sm text-gray-600">
                     <p>Bạn là người tạo case này</p>
                     <p className="mt-2">
                       Trạng thái: {getStatusBadge(workCase.workCase?.status || workCase.status)}
                     </p>
                   </div>
                 )}
              </div>

              {/* Timeline */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-lg font-medium text-gray-900 mb-3">Timeline</h3>
                <div className="space-y-3">
                  {timelineNotes.length === 0 ? (
                    <p className="text-gray-500 text-sm">Chưa có sự kiện nào</p>
                  ) : (
                    timelineNotes.map((n) => (
                      <div key={`tl-${n.id}`} className="flex items-start space-x-3">
                        <div className={`w-2 h-2 ${getEventColor(n.event)} rounded-full mt-2`}></div>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900">{renderEventText(n)}</p>
                          <p className="text-xs text-gray-500">{formatDate(n.created_at)}</p>
                          {/* Không hiển thị nội dung ghi chú trong timeline */}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkCaseDetail;
